"use client";

import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { Loader2, UserPlus } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type SharedUserTagSuggestion = {
  id: string;
  handle: string | null;
  label: string;
  subtitle?: string | null;
  avatarUrl?: string | null;
  disabledReason?: string | null;
};

type SharedUserTagPickerProps<TSuggestion extends SharedUserTagSuggestion> = {
  queryKey: readonly unknown[];
  searchFn: (query: string) => Promise<TSuggestion[]>;
  onSelect: (suggestion: TSuggestion) => void;
  disabled?: boolean;
  buttonLabel?: ReactNode;
  selectedUserIds?: Set<string>;
  currentUserId?: string;
  allowCurrentUserReselect?: boolean;
  className?: string;
  buttonClassName?: string;
  iconClassName?: string;
  placeholder?: string;
  title?: string;
  groupHeading?: string;
  emptyLabel?: string;
  duplicateLabel?: string;
  minQueryLength?: number;
  /**
   * Called instead of opening the popover. Return `true` to swallow the open —
   * for callers whose search cannot run yet (a signed-out checkout, say) and
   * who would rather steer the viewer somewhere than show an empty list.
   *
   * Deliberately not `disabled`: a greyed-out control reads as "broken", and
   * the whole point here is to hand the interaction somewhere useful.
   */
  interceptOpen?: () => boolean;
};

export function SharedUserTagPicker<
  TSuggestion extends SharedUserTagSuggestion,
>({
  queryKey,
  searchFn,
  onSelect,
  disabled,
  buttonLabel = "Tag user",
  selectedUserIds,
  currentUserId,
  allowCurrentUserReselect = false,
  className,
  buttonClassName,
  iconClassName,
  placeholder = "Search users...",
  title = "Tag a user",
  groupHeading = "Users",
  emptyLabel = "No users found",
  duplicateLabel = "Already tagged",
  minQueryLength = 1,
  interceptOpen,
}: SharedUserTagPickerProps<TSuggestion>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 200);
  const trimmedQuery = debouncedSearch.trim();
  const queryEnabled =
    open && !disabled && trimmedQuery.length >= minQueryLength;

  const { data: suggestions = [], isFetching } = useQuery<TSuggestion[]>({
    queryKey: [...queryKey, trimmedQuery],
    enabled: queryEnabled,
    queryFn: () => searchFn(trimmedQuery),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });

  const isAlreadySelected = useCallback(
    (suggestion: TSuggestion) =>
      Boolean(
        selectedUserIds?.has(suggestion.id) &&
          (!allowCurrentUserReselect || suggestion.id !== currentUserId),
      ),
    [allowCurrentUserReselect, currentUserId, selectedUserIds],
  );

  const handleSelect = useCallback(
    (suggestion: TSuggestion) => {
      if (suggestion.disabledReason || isAlreadySelected(suggestion)) {
        return;
      }

      onSelect(suggestion);
      setOpen(false);
      setSearch("");
    },
    [isAlreadySelected, onSelect],
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next && interceptOpen?.()) return;
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild nativeButton>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          className={cn(
            "h-full gap-2 border-brand/40 text-text-primary hover:bg-surface-alt",
            buttonClassName,
            className,
          )}
        >
          <UserPlus className={cn("h-3.5 w-3.5 text-brand", iconClassName)} />
          {buttonLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-80 border border-border bg-surface/95 p-0 text-text-primary shadow-2xl shadow-brand/20 backdrop-blur-xl"
      >
        <Command
          shouldFilter={false}
          className="bg-transparent text-text-primary [&_[cmdk-input]]:placeholder:text-text-secondary"
        >
          <div className="flex items-center justify-between border-b border-border bg-surface-alt/70 px-3 py-2 text-xs text-text-secondary">
            <span>{title}</span>
            {isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          </div>
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder={placeholder}
            disabled={disabled}
          />
          <CommandList>
            <CommandEmpty>
              {trimmedQuery.length === 0
                ? "Start typing to search"
                : trimmedQuery.length < minQueryLength
                  ? `Type at least ${minQueryLength} characters`
                  : emptyLabel}
            </CommandEmpty>
            <CommandGroup heading={groupHeading}>
              {suggestions.map((suggestion) => {
                const alreadySelected = isAlreadySelected(suggestion);
                const disabledReason = alreadySelected
                  ? duplicateLabel
                  : suggestion.disabledReason;
                const disabledOption = Boolean(disabledReason || disabled);

                return (
                  <CommandItem
                    key={suggestion.id}
                    value={suggestion.id}
                    disabled={disabledOption}
                    onSelect={() => handleSelect(suggestion)}
                    className={cn(
                      "flex items-center gap-3 py-2",
                      disabledOption && "opacity-70",
                    )}
                  >
                    <Avatar className="h-8 w-8 border border-border">
                      <AvatarImage src={suggestion.avatarUrl ?? undefined} />
                      <AvatarFallback className="text-xs">
                        {initialsFromLabel(suggestion.label)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex min-w-0 flex-1 flex-col text-sm">
                      <span className="truncate font-medium text-text-primary">
                        {suggestion.label}
                      </span>
                      {suggestion.handle ? (
                        <span className="truncate text-xs text-text-secondary">
                          @{suggestion.handle}
                        </span>
                      ) : null}
                      {suggestion.subtitle ? (
                        <span className="truncate text-xs text-text-tertiary">
                          {suggestion.subtitle}
                        </span>
                      ) : null}
                      {disabledReason ? (
                        <span className="text-xs text-error">
                          {disabledReason}
                        </span>
                      ) : null}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function initialsFromLabel(value: string) {
  return value
    .split(" ", 2)
    .map((segment) => segment[0] ?? "")
    .join("")
    .toUpperCase()
    .slice(0, 2);
}
